<template>
  <div>
    <NuxtLoadingIndicator :color="runtimeConfig.public.colorPrimary" />
    <Suspense>
      <template #default>
        <NuxtLayout
          :name="layoutName"
          class="app-entry"
        >
          <v-app>
            <div
              v-if="pageLoading || processingAuth"
              class="w-full h-dvh flex items-center justify-center"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                class="mt-6"
              />
            </div>

            <LazyNuxtPage v-else />
          </v-app>
        </NuxtLayout>
      </template>
      <template #fallback>
        <div class="w-full h-full flex items-center justify-center">
          <v-progress-circular
            indeterminate
            color="primary"
            class="mt-6"
          />
        </div>
      </template>
    </Suspense>
  </div>
</template>

<script setup lang="ts">
import { authPages, userPages, dashboardPages } from "./constants/navigation";
import { useProfileStore } from "./stores/profile";
import { CustomTitleSymbol } from "./types/symbols";
import type { LayoutKey } from "#build/types/layouts";

const colorMode = useColorMode();
if (import.meta.client) {
  colorMode.init(); // runs synchronously during setup, before first render — avoids Vuetify FOUC
}
const sessionStore = useSessionStore();
await isModuleReady(sessionStore);
const profileStore = useProfileStore();
profileStore.fetchProfile();
const notificationStore = useNotificationStore();
const { logout } = useAuth();

preloadRouteComponents("/");
const windowHistory = ref<History>();
const runtimeConfig = useRuntimeConfig();

const pageLoading = ref(true);
setTimeout(() => {
  pageLoading.value = false;
}, 500);

onErrorCaptured((err, vm, info, ...otherArgs: unknown[]) => {
  console.log("an error occurred --->", err, vm, info, otherArgs);
});

const notifcationsLoaderJobKey = ref<NodeJS.Timeout>();
onMounted(() => {
  if ("Notification" in window) {
    if (window.Notification.permission === "granted") {
      notificationStore.notificationsAreAllowed = true;
    } else notificationStore.notificationsAreAllowed = false;

    const requestNotificationAccess = (event: Event) => {
      if (notificationStore.notificationsAreAllowed) return;
      notificationStore.confirmNotificationsAreAllowed();
      event.target?.removeEventListener("click", requestNotificationAccess);
      event.target?.removeEventListener("touchmove", requestNotificationAccess);
    };
    windowHistory.value = window.history;
    document.body.addEventListener("click", requestNotificationAccess, {
      once: true,
    });
    document.body.addEventListener("touchmove", requestNotificationAccess, {
      once: true,
    });
  }

  const inactivityTime = 3 * 60 * 1000;
  let timeout: NodeJS.Timeout;

  function startLogoutTimer() {
    timeout = setTimeout(() => logout(), inactivityTime);
  }

  document.addEventListener("visibilitychange", function () {
    if (!runtimeConfig.public.autoLogout) return;
    if (!sessionStore.isLoggedIn) return;
    if (document.hidden) {
      startLogoutTimer();
    } else {
      if (timeout) {
        clearTimeout(timeout);
      }
    }
  });

  notifcationsLoaderJobKey.value = setInterval(
    () => {
      notificationStore.notificationsFetchKey = Date.now();
    },
    5 * 60 * 1000,
  );
});

onBeforeUnmount(() => {
  if (notifcationsLoaderJobKey.value) {
    clearInterval(notifcationsLoaderJobKey.value);
  }
});

const { isLoggedIn, processingAuth } = storeToRefs(sessionStore);
const route = useRoute();
provide(CustomTitleSymbol, ref(""));
const layoutName = asyncComputed<LayoutKey>(() => {
  switch (true) {
    case routeIsMemberOfGroup(String(route.name), authPages):
      return "auth";
    case routeIsMemberOfGroup(String(route.name), dashboardPages):
      return "dashboard";
    case routeIsMemberOfGroup(String(route.name), userPages):
      return "default";
    default:
      return isLoggedIn.value ? "dashboard" : "empty";
  }
});

watch(isLoggedIn, (value) => {
  if (value) {
    notificationStore.notificationsFetchKey = Date.now();
  }
});

function routeIsMemberOfGroup(route: string, group: string[]) {
  return (
    group.includes(route) || group.some((Iroute) => route.startsWith(Iroute))
  );
}

const { initializeAzureAD } = useMSAL();
initializeAzureAD();
</script>

<style lang="scss">
@use "animate.css";
@use "@/assets/styles/modules/vcalendar";
.app-entry {
  .dx-overlay-wrapper {
    z-index: 2500 !important;
    > .dx-overlay-content {
      z-index: 2500 !important;
    }
  }
  .ql-editor.ql-blank {
    padding: 0;
  }

  .v-expansion-panel:not(:first-child)::after {
    border: none;
  }

  svg {
    path[fill] {
      &:not([fill="none"]) {
        stroke: none;
      }
    }
  }
  svg.fill-path {
    path[fill-rule] {
      fill: currentColor;
    }
  }
}
</style>
